import { CreateFlowcodeStateType } from '@app/common/src/types/createFlowcode.types'
import { FcShape } from '@dtx-company/flowcode-generator-types/src'
import {
  FlowcodeTemplateType,
  GetFlowcodeTemplatesResponseType
} from '@dtx-company/inter-app/src/types/flowcode'
import { GET_FLOWCODE_TEMPLATES_QUERY } from '../gql/queries/flowcode'
import { OutputSelector, createSelector } from '@reduxjs/toolkit'
import { getIsFlowcodeInvertible, isFlowcodeCustomizable } from '@app/common/src/utils/flowcode'
import { shallowEqual, useSelector } from 'react-redux'
import { useInjectReducer } from 'redux-injectors'
import reducer, {
  generatorOptions,
  initialCreateFlowcodeState
} from '../redux/slices/createFlowcodeSlice'
import useSWR from 'swr'

type CreateFlowcodeRootState = {
  createFlowcode: CreateFlowcodeStateType
}

const getInitialState = (
  flowcodeTemplates?: FlowcodeTemplateType[],
  flowcodeShape?: FcShape
): CreateFlowcodeStateType => {
  if (flowcodeTemplates && flowcodeTemplates.length > 0) {
    const t = flowcodeTemplates[0]
    const template = {
      ...t,
      isInvertible: getIsFlowcodeInvertible(t),
      options: t.configuration ? JSON.parse(t.configuration) : undefined
    }
    const state: CreateFlowcodeStateType = {
      ...initialCreateFlowcodeState,
      template,
      isCustomizable: isFlowcodeCustomizable(template)
    }

    if (!template.options?.isInvertible) {
      state.theme = initialCreateFlowcodeState.theme
    }

    if (flowcodeShape) {
      state.borderShape = flowcodeShape
    }

    state.options = generatorOptions(state)
    return state
  } else {
    return initialCreateFlowcodeState
  }
}

const key = 'createFlowcode'

const createCreateFlowcodeSelector = (
  flowcodeTemplates?: FlowcodeTemplateType[],
  flowcodeShape?: FcShape
): OutputSelector<
  ((state: CreateFlowcodeRootState) => CreateFlowcodeStateType)[],
  CreateFlowcodeStateType,
  (res: CreateFlowcodeStateType) => CreateFlowcodeStateType
> =>
  createSelector(
    (state: CreateFlowcodeRootState) => {
      if (!state[key]) {
        state[key] = getInitialState(flowcodeTemplates, flowcodeShape)
      }
      return state[key]
    },
    state => state
  )

export const useCreateFlowcodeState = (): CreateFlowcodeStateType => {
  const { data } = useSWR<GetFlowcodeTemplatesResponseType>(GET_FLOWCODE_TEMPLATES_QUERY, {
    revalidateOnFocus: false,
    revalidateOnMount: false
  })
  const defaultShape = FcShape.CIRCLE
  const templates = data?.flowcodeTemplates

  useInjectReducer({ key, reducer })
  return useSelector<CreateFlowcodeRootState, CreateFlowcodeStateType>(
    createCreateFlowcodeSelector(templates, defaultShape),
    shallowEqual
  )
}
